<template>
    <div class="schedule-grid-wrapper" @mouseleave="resort">
        <div class="schedule-toolbar">
            <div v-if="friendsList.length" class="friend-filter">
                <template v-if="friendsList.length <= inlineFriendThreshold">
                    <a
                        v-for="friend in friendsList"
                        :key="friend.id"
                        href="#"
                        class="friend-filter-link"
                        :class="{ active: friendFilter === friend.id }"
                        @click.prevent="toggleFriendFilter(friend.id)"
                    >
                        {{ friend.name }}
                    </a>
                </template>
                <select
                    v-else
                    v-model="friendFilter"
                    aria-label="Filter by friend"
                >
                    <option :value="null">All friends</option>
                    <option
                        v-for="friend in friendsList"
                        :key="friend.id"
                        :value="friend.id"
                    >
                        {{ friend.name }}
                    </option>
                </select>
            </div>
            <ul class="schedule-legend">
                <li
                    v-for="[value, label] in schedulePreferences"
                    :key="value"
                    :class="`state-${value}`"
                >
                    <span class="swatch"></span>
                    {{ label }}
                </li>
                <li class="state-hint">
                    <span class="swatch"></span>
                    Season
                </li>
                <li class="state-done">
                    <span class="swatch"></span>
                    Done
                </li>
            </ul>
        </div>
        <table class="schedule-grid">
            <thead>
                <tr>
                    <th class="schedule-name">Name</th>
                    <th class="schedule-narrow-header">Total</th>
                    <th v-if="showDaysOff" class="schedule-narrow-header">
                        Days off
                    </th>
                    <th
                        v-if="showBudget"
                        class="schedule-narrow-header schedule-narrow-header--wide"
                    >
                        Budget
                    </th>
                    <th
                        v-for="label in monthLabels"
                        :key="label"
                        class="schedule-month-header"
                    >
                        {{ label }}
                    </th>
                </tr>
            </thead>
            <TransitionGroup tag="tbody" name="schedule-row">
                <tr v-for="row in displayRows" :key="row.id">
                    <td class="schedule-name">
                        <span
                            v-if="isShared(row)"
                            class="material-symbols-outlined shared-icon"
                            aria-hidden="true"
                        >
                            groups
                        </span>
                        <button
                            type="button"
                            class="schedule-name-inner schedule-name-link"
                            @click="openRow(row)"
                        >
                            <span
                                class="material-symbols-outlined type-icon"
                                :title="row.type"
                            >
                                {{ typeIcon(row) }}
                            </span>
                            <span :title="row.name">{{
                                displayName(row)
                            }}</span>
                        </button>
                    </td>
                    <ScheduleGridNumberCell
                        :model-value="row.planned_days"
                        :label="`Total days for ${row.name}`"
                        :fixed="
                            !row.is_owner || row.derived.start_month !== null
                        "
                        @update:model-value="
                            onFieldChange(row, 'planned_days', $event)
                        "
                    />
                    <ScheduleGridNumberCell
                        v-if="showDaysOff"
                        :model-value="row.me.vacation_days"
                        :label="`Days off for ${row.name}`"
                        @update:model-value="
                            onFieldChange(row, 'vacation_days', $event, true)
                        "
                    />
                    <ScheduleGridNumberCell
                        v-if="showBudget"
                        :model-value="row.me.money"
                        :label="`Budget for ${row.name}`"
                        wide
                        :max="1000000"
                        step="10"
                        :prefix="currency"
                        @update:model-value="
                            onFieldChange(row, 'money', $event, true)
                        "
                    />
                    <ScheduleGridCell
                        v-for="(label, i) in monthLabels"
                        :key="i + 1"
                        :model-value="row.me.months[i + 1]"
                        :month="label"
                        :overlaps="
                            row.participants.map(
                                (participant) => participant.months[i + 1]
                            )
                        "
                        :hint="row.derived.ideal_months.includes(i + 1)"
                        :done="row.derived.done_months.includes(i + 1)"
                        @update:model-value="onMonthChange(row, i + 1, $event)"
                    />
                </tr>
                <tr v-if="!filteredRows.length" key="empty">
                    <td class="empty-state" :colspan="emptyColspan">
                        No rows yet — add a trip, idea, or your own entry below.
                    </td>
                </tr>
            </TransitionGroup>
        </table>

        <RowDetailModal
            v-if="activeRow"
            :row="activeRow"
            :year="year"
            :excluded="excludedForModal"
            @close="closeRow"
            @remove="$emit('remove', $event)"
            @update="onRowUpdate"
        />
    </div>
</template>

<script>
import ScheduleGridCell from "js/components/tables/ScheduleGridCell";
import ScheduleGridNumberCell from "js/components/tables/ScheduleGridNumberCell";
import RowDetailModal from "js/components/modals/RowDetailModal";
import { edit, handleResponse } from "js/lib/server.js";
import { shortenScheduleName } from "js/lib/scheduleName.js";

const TYPE_ICONS = {
    trip: "trip",
    idea: "explore",
    custom: "globe_location_pin",
};

// A row you don't own trades its type icon for a generic "not mine" one.
const NOT_OWNER_ICON = "move";

function sortKey(row) {
    const candidates = [];
    if (Number.isInteger(row.derived.start_month)) {
        candidates.push(row.derived.start_month);
    }
    candidates.push(
        Math.min(
            ...[
                Infinity,
                ...Array.from({ length: 12 }, (_, i) => i + 1).filter((i) => {
                    return (
                        row.me.months[i] === "plan" ||
                        row.me.months[i] === "commit"
                    );
                }),
            ]
        )
    );
    return Math.min(...candidates);
}

export default {
    name: "ScheduleGrid",
    components: { ScheduleGridCell, ScheduleGridNumberCell, RowDetailModal },
    props: {
        rows: {
            type: Array,
            required: true,
        },
        year: {
            type: Number,
            required: true,
        },
        friendsList: {
            type: Array,
            default: () => [],
        },
        inlineFriendThreshold: {
            type: Number,
            default: 5,
        },
        showDaysOff: {
            type: Boolean,
            default: false,
        },
        showBudget: {
            type: Boolean,
            default: false,
        },
        currency: {
            type: String,
            default: "$",
        },
        excluded: {
            type: Array,
            default: () => [],
        },
    },
    emits: ["remove", "save-error"],
    data() {
        return {
            schedulePreferences: this.$enumOptions("schedule_preference"),
            activeRow: null,
            friendFilter: null,
            // Display order, refreshed only at safe moments (see `resort`) —
            // not a live computed, so editing a row's months doesn't yank it
            // out from under the cursor mid-click.
            orderedIds: [],
            // Per-row patches waiting to be sent, keyed by row id. `months`
            // always holds the complete current map, not just the changed
            // key, since the endpoint replaces the whole thing on save.
            pendingPatches: {},
        };
    },
    computed: {
        monthLabels() {
            return Array.from({ length: 12 }, (_, i) =>
                new Date(this.year, i).toLocaleString("en-us", {
                    month: "short",
                })
            );
        },
        // Rows narrowed down to the selected friend, if any. When a friend
        // is selected, each matching row's own `participants` list is
        // narrowed to just them too, so month-cell overlap fades reflect
        // only that person rather than everyone blended together.
        filteredRows() {
            if (!this.friendFilter) {
                return this.rows;
            }
            return this.rows
                .filter((row) =>
                    (row.participants ?? []).some(
                        (participant) => participant.id === this.friendFilter
                    )
                )
                .map((row) => ({
                    ...row,
                    participants: row.participants.filter(
                        (participant) => participant.id === this.friendFilter
                    ),
                }));
        },
        // Rows in their last-sorted order. Freshly added rows (not yet in
        // `orderedIds`) keep their natural order and land at the end, until
        // the next `resort()` places them properly.
        displayRows() {
            const rank = new Map(this.orderedIds.map((id, i) => [id, i]));
            const base = this.orderedIds.length;
            return this.filteredRows
                .map((row, i) => ({ row, key: rank.get(row.id) ?? base + i }))
                .sort((a, b) => a.key - b.key)
                .map((entry) => entry.row);
        },
        // Identifies the current row *set* (add/remove/year change) without
        // reacting to in-place edits on existing rows — that distinction is
        // what lets `resort()` stay out of the way while editing.
        rowIdSignature() {
            return this.rows.map((row) => row.id).join("|");
        },
        // Name + Total + the 12 month columns, plus one for each
        // constraint column currently shown.
        emptyColspan() {
            return 14 + (this.showDaysOff ? 1 : 0) + (this.showBudget ? 1 : 0);
        },
        excludedForModal() {
            if (!this.activeRow) {
                return this.excluded;
            }
            return this.excluded.filter(
                (e) =>
                    !(
                        e.type === this.activeRow.type &&
                        e.refId === this.activeRow.itemable_id
                    )
            );
        },
    },
    watch: {
        // The friend list is scoped to a year, so a friend id kept from a
        // previous year could match nothing — or the wrong person.
        year() {
            this.friendFilter = null;
        },
        rowIdSignature: {
            immediate: true,
            handler() {
                this.resort();
            },
        },
        friendFilter() {
            this.resort();
        },
    },
    mounted() {
        window.addEventListener("beforeunload", this.flushPendingChanges);
        window.addEventListener("pagehide", this.flushPendingChanges);
    },
    beforeUnmount() {
        window.removeEventListener("beforeunload", this.flushPendingChanges);
        window.removeEventListener("pagehide", this.flushPendingChanges);
        this.flushPendingChanges();
    },
    methods: {
        typeIcon(row) {
            if (!row.is_owner) {
                return NOT_OWNER_ICON;
            }
            return TYPE_ICONS[row.type] || TYPE_ICONS.custom;
        },
        isShared(row) {
            return (row.participants ?? []).length > 0;
        },
        displayName(row) {
            if (row.type === "custom") {
                return row.name;
            }
            return shortenScheduleName(row.name, row.derived.countries);
        },
        openRow(row) {
            // displayRows/filteredRows can hand back a shallow copy (when a
            // friend filter is active). Edits go through activeRow, so it
            // needs to be the genuine row object — not a copy that would
            // silently lose the edit.
            this.activeRow = this.rows.find((r) => r.id === row.id) ?? row;
        },
        // The one edit that stays immediate rather than batched — picking a
        // different trip/idea is deliberate and infrequent, and its
        // response is where the row's fresh `derived` comes from.
        async onRowUpdate(fields) {
            const target = this.activeRow;
            try {
                const updated = await edit(
                    `/api/travel-schedule/${this.year}/items/${target.id}`,
                    fields
                ).then(handleResponse);
                Object.assign(target, updated);
            } catch (err) {
                console.error(err);
                this.$emit("save-error");
            }
        },
        closeRow() {
            // Re-pointing a row at a different trip/idea can change its
            // derived.start_month, so the order may need to catch up once
            // the modal is done.
            this.activeRow = null;
            this.resort();
        },
        toggleFriendFilter(id) {
            this.friendFilter = this.friendFilter === id ? null : id;
        },
        resort() {
            this.orderedIds = [...this.filteredRows]
                .sort((a, b) => {
                    const ka = sortKey(a);
                    const kb = sortKey(b);
                    return ka !== kb ? ka - kb : a.name.localeCompare(b.name);
                })
                .map((row) => row.id);
            this.flushPendingChanges();
        },
        queuePatch(row, patch) {
            this.pendingPatches[row.id] = {
                ...(this.pendingPatches[row.id] || {}),
                ...patch,
            };
        },
        onFieldChange(row, field, value, isGridField) {
            (isGridField ? row.me : row)[field] = value;
            this.queuePatch(row, { [field]: value });
        },
        onMonthChange(row, month, value) {
            if (value === null) {
                delete row.me.months[month];
            } else {
                row.me.months[month] = value;
            }
            this.queuePatch(row, { months: { ...row.me.months } });
        },
        async flushPendingChanges() {
            const patches = this.pendingPatches;
            this.pendingPatches = {};
            const ids = Object.keys(patches);
            if (!ids.length) {
                return;
            }
            const results = await Promise.allSettled(
                ids.map((id) =>
                    edit(
                        `/api/travel-schedule/${this.year}/items/${id}`,
                        patches[id]
                    )
                )
            );
            if (results.some((r) => r.status === "rejected")) {
                results.forEach(
                    (r) => r.status === "rejected" && console.error(r.reason)
                );
                this.$emit("save-error");
            }
        },
    },
};
</script>
